/// Home-screen settings modal. Currently surfaces actions for
/// re-viewing the help flows the player may have dismissed,
/// plus a link out to the privacy policy hosted on the public
/// docs site (same URL the iOS app uses, so policy edits land
/// in one place).
///
/// More settings (sound, theme, units) can land here as separate
/// rows without restructuring the modal.

import { useState } from 'react'
import { persistentArrivalsEnabled, setPersistentArrivalsEnabled } from '../state/store'
import { resetTutorialSeen } from './TutorialGuide'
import { openHelpdesk } from '../auth/helpdesk'
import { RedeemCodeModal } from './RedeemCodeModal'
import {
  isAnalyticsOptedOut,
  setAnalyticsOptOut,
} from '../firebase/app'
import { setConsent } from '../data/consent'
import { isVoiceEnabled, setVoicePref, speak } from '../audio/VoiceManager'

const PRIVACY_POLICY_URL =
  'https://flatoutatc.liveflatout.com/docs/flatout-atc/policies/privacy-policy'

export function SettingsModal(props: {
  onShowWelcome: () => void
  onDismiss: () => void
}) {
  const [tutorialReset, setTutorialReset] = useState(false)
  const [redeemOpen, setRedeemOpen] = useState(false)
  const [analyticsOff, setAnalyticsOff] = useState(() => isAnalyticsOptedOut())
  const [voiceOn, setVoiceOn] = useState(() => isVoiceEnabled())
  const [arrivalsPinned, setArrivalsPinned] = useState(() => persistentArrivalsEnabled())

  const handleResetTutorial = () => {
    resetTutorialSeen()
    setTutorialReset(true)
  }

  return (
    <div
      className="pointer-events-auto absolute inset-0 z-30 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={props.onDismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-xl bg-slate-900 p-5 text-slate-100 shadow-2xl ring-1 ring-white/10"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-wide">Settings</h2>
          <button
            onClick={props.onDismiss}
            className="text-xs text-slate-400 hover:text-white"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <section className="mt-4">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Help & onboarding
          </div>
          <ul className="space-y-2">
            <li>
              <SettingsRow
                title="Show welcome again"
                detail="Re-watch the first-visit walkthrough."
                actionLabel="Show"
                onAction={() => {
                  props.onDismiss()
                  props.onShowWelcome()
                }}
              />
            </li>
            <li>
              <SettingsRow
                title="Replay TUTORIAL guide"
                detail={
                  tutorialReset
                    ? 'Done — the guide will appear next time you enter the TUTORIAL level.'
                    : 'Reset the step-by-step prompts on the TUTORIAL level.'
                }
                actionLabel={tutorialReset ? 'Reset ✓' : 'Reset'}
                onAction={handleResetTutorial}
                done={tutorialReset}
              />
            </li>
          </ul>
        </section>

        <section className="mt-5">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Account
          </div>
          <ul className="space-y-2">
            <li>
              <SettingsRow
                title="Redeem an iOS code"
                detail="Used the iOS app's “Use my purchases on the web” button? Apply the code here."
                actionLabel="Redeem"
                onAction={() => setRedeemOpen(true)}
              />
            </li>
          </ul>
        </section>

        <section className="mt-5">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Support
          </div>
          <ul className="space-y-2">
            <li>
              <SettingsRow
                title="Contact Support"
                detail="Open a help thread or reply to an existing one."
                actionLabel="Open"
                onAction={() => {
                  props.onDismiss()
                  openHelpdesk()
                }}
              />
            </li>
          </ul>
        </section>

        <section className="mt-5">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Audio
          </div>
          <ul className="space-y-2">
            <li>
              <SettingsRow
                title="ATC voice readbacks"
                detail={
                  voiceOn
                    ? 'Spoken acknowledgements + radio blip on every transmission.'
                    : 'Voice off — readbacks show as on-screen toasts only.'
                }
                actionLabel={voiceOn ? 'Turn off' : 'Turn on'}
                onAction={() => {
                  const next = !voiceOn
                  setVoicePref(next ? 'on' : 'off')
                  setVoiceOn(next)
                  // Test ping when turning ON so the player hears
                  // a sample at the moment they flip the switch
                  // (also primes the autoplay path on Safari).
                  if (next) speak('Voice readbacks enabled.')
                }}
              />
            </li>
          </ul>
        </section>

        <section className="mt-5">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Gameplay
          </div>
          <ul className="space-y-2">
            <li>
              <SettingsRow
                title="Persistent arrival check-ins"
                detail={
                  arrivalsPinned
                    ? 'New arrivals pin under the wind indicator until you select or command them.'
                    : 'Arrival check-ins show as fading toasts only.'
                }
                actionLabel={arrivalsPinned ? 'Turn off' : 'Turn on'}
                onAction={() => {
                  const next = !arrivalsPinned
                  setPersistentArrivalsEnabled(next)
                  setArrivalsPinned(next)
                }}
              />
            </li>
          </ul>
        </section>

        <section className="mt-5">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Privacy
          </div>
          <ul className="space-y-2">
            <li>
              <SettingsRow
                title="Analytics"
                detail={
                  analyticsOff
                    ? 'Analytics off — anonymized event tracking is disabled.'
                    : 'Anonymized event tracking helps tune the game. No personal data shared.'
                }
                actionLabel={analyticsOff ? 'Turn on' : 'Turn off'}
                onAction={() => {
                  const next = !analyticsOff
                  setAnalyticsOptOut(next)
                  setAnalyticsOff(next)
                  // Keep the consent record in lockstep with the
                  // toggle: turning analytics off downgrades to
                  // 'essential', turning it on upgrades to 'all'
                  // (also re-enables the helpdesk widget loader).
                  setConsent(next ? 'essential' : 'all')
                }}
              />
            </li>
          </ul>
        </section>

        <section className="mt-5">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            About
          </div>
          <ul className="space-y-2">
            <li>
              <SettingsRow
                title="Privacy Policy"
                detail="How player data is collected and handled."
                actionLabel="Open ↗"
                onAction={() => {
                  window.open(
                    PRIVACY_POLICY_URL,
                    '_blank',
                    'noopener,noreferrer',
                  )
                }}
              />
            </li>
          </ul>
        </section>

        <div className="mt-5 flex justify-end">
          <button
            onClick={props.onDismiss}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-500"
          >
            Done
          </button>
        </div>
      </div>
      {redeemOpen && (
        <RedeemCodeModal onDismiss={() => setRedeemOpen(false)} />
      )}
    </div>
  )
}

function SettingsRow(props: {
  title: string
  detail: string
  actionLabel: string
  onAction: () => void
  done?: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md bg-slate-800/60 px-3 py-2 ring-1 ring-white/5">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-100">{props.title}</div>
        <div className="mt-0.5 text-xs text-slate-400">{props.detail}</div>
      </div>
      <button
        onClick={props.onAction}
        disabled={props.done}
        className={
          'shrink-0 rounded-md px-3 py-1.5 text-xs font-semibold ring-1 transition-colors ' +
          (props.done
            ? 'cursor-default bg-emerald-900/40 text-emerald-200 ring-emerald-500/30'
            : 'bg-slate-700 text-slate-100 ring-white/10 hover:bg-slate-600')
        }
      >
        {props.actionLabel}
      </button>
    </div>
  )
}
